import { useEffect, useRef, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  addDoc,
  getDoc,
  serverTimestamp,
  runTransaction,
  increment,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../../firebase";
import { exportTokensToExcel } from "../../utils/exportTokensToExcel";
import { getTodayIST } from "../../utils/dateUtils";
import { useNavigate } from "react-router-dom";
import { fullReset } from "./resetData";
import TokenQueueHeader from "./TokenQueueHeader";
import { isWhitelistedIP } from "../../utils/ipWhitelist";
import { clinicData } from "../../mock";
import TokenSummaryCards from "./TokenSummaryCards";
const VISIT_TYPE_HELPER = {
  NEW: "This is your first visit to VPJC for consultation or treatment.",
  REVISIT: "You have visited VPJC before and coming back after a long gap.",
  REVIEW:
    "You visited recently and are returning for a doctor review or report discussion.",
  THERAPY: "You are coming for a scheduled or ongoing therapy session",
};

const CLINIC_LOCATIONS = [
  {
    name: "Gajuwaka Branch",
    lat: 17.6799221,
    lng: 83.1987568,
  },
  {
    name: "Kurmannapalem Branch",
    lat: 17.6862913,
    lng: 83.165883,
  },
];

const ALLOWED_RADIUS_METERS = 300;

function getDistanceInMeters(lat1, lng1, lat2, lng2) {
  const toRad = (value) => (value * Math.PI) / 180;
  const R = 6371000;

  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;

  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const chunkArray = (arr, size) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

export default function TokenQueue() {
  const [visits, setVisits] = useState([]);
  const [todayTreatmentEntries, setTodayTreatmentEntries] = useState([]);
  const [todayPackagePayments, setTodayPackagePayments] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [visitingFor, setVisitingFor] = useState("");
  const [visitingForOther, setVisitingForOther] = useState("");
  const [visitType, setVisitType] = useState("");
  const [loading, setLoading] = useState(false);
  const [createErrors, setCreateErrors] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [filter, setFilter] = useState("today");
  const [selectedDate, setSelectedDate] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [detectingBranch, setDetectingBranch] = useState(true);
  const [locationError, setLocationError] = useState("");
  const [visitMode, setVisitMode] = useState(""); // "", "OLD", "NEW"
  const [patientFound, setPatientFound] = useState(false);
  const [matchedPatients, setMatchedPatients] = useState([]);
  const [checkingPatient, setCheckingPatient] = useState(false);
  const [mobileError, setMobileError] = useState("");
  const [adminBranch, setAdminBranch] = useState(null);
  const [referredBy, setReferredBy] = useState("");
  const matchedPatientsBackup = useRef([]);
  const [inlineDetails, setInlineDetails] = useState({});
  const [allVisits, setAllVisits] = useState([]);
  const [endDate, setEndDate] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 15;
  const navigate = useNavigate();

  const navigateToPatientPage = (vpjcId) => {
    navigate(`/admin-dashboard?tab=patients&vpjcId=${vpjcId}`);
  };

  const [search, setSearch] = useState("");

  // ✅ AUTO-DETECT BRANCH ON PAGE LOAD
  useEffect(() => {
    const initBranch = async () => {
      try {
        const branch = await checkAdminLocation(); // already exists
        setAdminBranch(branch);
        setBranchFilter(branch.name); // ✅ PREFILL FILTER
        setLocationError("");
      } catch (err) {
        console.warn("Branch auto-detection failed");
        setLocationError(err);
      } finally {
        setDetectingBranch(false);
      }
    };

    initBranch();
  }, []);

  const normalizeName = (n) => n.trim().toLowerCase();

  const generateRandomNumericVPJCId = () => {
    const num = Math.floor(100000 + Math.random() * 900000);
    return `VPJC-${num}`;
  };

  const checkAdminLocation = async () => {
    const bypass = await isWhitelistedIP();
    console.log("IP Bypass:", bypass);
    if (bypass) {
      console.log("✅ Whitelisted IP detected");
      return CLINIC_LOCATIONS[0];
    }
    console.log("❌ Not a whitelisted IP, checking GPS");
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject("GPS not supported");
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;

          let matchedBranch = null;

          for (const clinic of CLINIC_LOCATIONS) {
            const distance = getDistanceInMeters(
              latitude,
              longitude,
              clinic.lat,
              clinic.lng,
            );

            if (distance <= ALLOWED_RADIUS_METERS) {
              matchedBranch = clinic;
              break;
            }
          }

          if (matchedBranch) {
            resolve(matchedBranch);
          } else {
            reject("Admin token allowed only at clinic location");
          }
        },
        () => reject("Location permission denied"),
        { enableHighAccuracy: true },
      );
    });
  };

  const getDateRange = () => {
    const today = getTodayIST();
    let fromDate = today;
    let toDate = today;

    // ✅ 🔥 PRIORITY: custom range
    if (selectedDate && endDate) {
      fromDate = selectedDate;
      toDate = endDate;

      // auto swap if wrong
      if (toDate < fromDate) {
        [fromDate, toDate] = [toDate, fromDate];
      }
    }

    // ✅ only start date
    else if (selectedDate) {
      fromDate = selectedDate;
      toDate = selectedDate;
    } else if (filter === "yesterday") {
      const d = new Date(today);
      d.setDate(d.getDate() - 1);
      fromDate = d.toISOString().split("T")[0];
      toDate = fromDate;
    } else if (filter === "7") {
      const d = new Date(today);
      d.setDate(d.getDate() - 6);
      fromDate = d.toISOString().split("T")[0];
      toDate = today;
    } else if (filter === "30") {
      const d = new Date(today);
      d.setDate(d.getDate() - 29);
      fromDate = d.toISOString().split("T")[0];
      toDate = today;
    } else if (filter === "60") {
      const d = new Date(today);
      d.setDate(d.getDate() - 59);
      fromDate = d.toISOString().split("T")[0];
      toDate = today;
    }

    return { fromDate, toDate };
  };

  const fetchVisits = () => {
    const { fromDate, toDate } = getDateRange();

    const q = branchFilter
      ? query(
          collection(db, "visits"),
          where("branch_name", "==", branchFilter),
        )
      : query(collection(db, "visits"));
    const unsubscribe = onSnapshot(q, async (snap) => {
      const visitsRaw = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((v) => v.visit_date >= fromDate && v.visit_date <= toDate)
        .sort(
          (a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0),
        );

      // 🔑 attach patient package info
      const patientIds = [...new Set(visitsRaw.map((v) => v.patient_id))];

      const patientMap = {};
      const chunks = chunkArray(patientIds, 30);

      for (const chunk of chunks) {
        const pSnap = await getDocs(
          query(collection(db, "patients"), where("__name__", "in", chunk)),
        );

        pSnap.forEach((doc) => {
          patientMap[doc.id] = doc.data();
        });
      }

      // ✅ MERGE patient → visit
      const list = visitsRaw.map((v) => {
        const p = patientMap[v.patient_id] || {};

        return {
          ...v,
          package_total_days: p.package_total_days,
          package_used_days: p.package_used_days,
          active_package: p.active_package,
          package_expiry_date: p.package_expiry_date,
          package_start_date: p?.package_start_date,
          patient_total_visits: p.visit_count || 0,
        };
      });

      setVisits(list);
    });

    return unsubscribe;
  };

  useEffect(() => {
    if (detectingBranch) return;

    const unsubscribe = fetchVisits(); // start realtime listener

    return () => {
      unsubscribe(); // cleanup old listener
    };
  }, [filter, selectedDate, endDate, branchFilter, detectingBranch]);

  useEffect(() => {
    const fetchTreatmentEntries = async () => {
      const snap = await getDocs(collection(db, "treatment_entries"));

      const list = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      setTodayTreatmentEntries(list);
    };

    fetchTreatmentEntries();
  }, []);
  useEffect(() => {
    const fetchPackagePayments = async () => {
      const snap = await getDocs(collection(db, "package_payments"));

      const list = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      setTodayPackagePayments(list);
    };

    fetchPackagePayments();
  }, []);

  const validateEditToken = () => {
    const errors = {};

    // Patient Name
    if (!editing.name?.trim()) {
      errors.name = "Patient name is required";
    }

    // Visiting For
    if (!editing.visiting_for) {
      errors.visiting_for = "Please select visiting for";
    }

    // If OTHER selected
    if (
      editing.visiting_for === "OTHER" &&
      !editing.visiting_for_other?.trim()
    ) {
      errors.visiting_for_other = "Please specify visiting person";
    }

    // Visit Type
    if (!editing.visit_type) {
      errors.visit_type = "Please select visit type";
    }
    if (
      editing.consultation_payment_status === "PAID" &&
      editing.consultation_fee > 0 &&
      !editing.amount_change_reason?.trim()
    ) {
      errors.amount_change_reason = "Reason is required for amount change";
    }
    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdate = async () => {
    if (!validateEditToken()) return;

    const visitRef = doc(db, "visits", editing.id);

    const updateData = {
      name: editing.name,
      visiting_for: editing.visiting_for,
      visit_type: editing.visit_type,
      updated_at: serverTimestamp(),
    };

    // ✅ CONSULTATION
    if (editing.consultation_payment_status === "PAID") {
      updateData.consultation_payment_status = "PAID";
      updateData.consultation_fee = Number(editing.consultation_fee || 0);
    } else {
      updateData.consultation_payment_status =
        editing.consultation_payment_status;
      updateData.consultation_fee = 0;
    }

    // ✅ TREATMENT
    if (editing.visit_category === "TREATMENT") {
      updateData.treatment_amount = Number(editing.treatment_amount || 0);
      updateData.treatment_payment_status =
        editing.treatment_payment_status || "PAID";
    }

    // ✅ REASON
    if (editing.amount_change_reason) {
      updateData.amount_change_reason = editing.amount_change_reason;
    }

    await updateDoc(visitRef, updateData);
    await updateDoc(visitRef, updateData);

    // 🔥 ADD THIS BLOCK
    if (editing.visit_category === "TREATMENT") {
      const amount = Number(editing.treatment_amount || 0);

      // 🔍 find existing treatment entry
      const snap = await getDocs(
        query(
          collection(db, "treatment_entries"),
          where("visit_id", "==", editing.id),
        ),
      );

      if (!snap.empty) {
        // ✅ update existing
        const docId = snap.docs[0].id;

        await updateDoc(doc(db, "treatment_entries", docId), {
          amount: amount,
          updated_at: serverTimestamp(),
        });
      } else {
        // ⚠️ fallback (rare case)
        await addDoc(collection(db, "treatment_entries"), {
          treatment_id: editing.treatment_id || null,
          patient_id: editing.patient_id,
          visit_id: editing.id,
          amount: amount,
          branch_name: editing.branch_name,
          date: new Date(),
          created_at: serverTimestamp(),
        });
      }
    }

    // 🔥 update local visits immediately
    setVisits((prev) =>
      prev.map((v) =>
        v.id === editing.id
          ? {
              ...v,
              name: editing.name,
              visiting_for: editing.visiting_for,
              visit_type: editing.visit_type,
              consultation_payment_status: editing.consultation_payment_status,
              consultation_fee: Number(editing.consultation_fee || 0),
            }
          : v,
      ),
    );

    // optional but safe
    setInlineDetails((prev) => {
      const updated = { ...prev };
      delete updated[editing.id];
      return updated;
    });

    setEditing(null);
    setEditErrors({});
  };

  const validateCreateToken = () => {
    const errors = {};

    if (!mobile.trim()) {
      errors.mobile = "Mobile number is required";
    } else if (!/^[6-9]\d{9}$/.test(mobile.trim())) {
      errors.mobile = "Enter valid 10-digit mobile number";
    }

    if (!name.trim()) {
      errors.name = "Patient name is required";
    }

    if (!visitingFor) {
      errors.visitingFor = "Please select visiting for";
    }
    if (visitingFor === "OTHER" && !visitingForOther.trim()) {
      errors.visitingForOther = "Please specify visiting for";
    }

    if (!visitType) {
      errors.visitType = "Please select visit type";
    }

    setCreateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // =========================
  // MANUAL CREATE TOKEN
  // =========================
  const handleCreateToken = async () => {
    if (!validateCreateToken()) return;

    setLoading(true);

    try {
      const today = getTodayIST();
      if (!adminBranch) {
        setLocationError("Clinic location not detected. Please refresh.");
        setLoading(false);
        return;
      }

      const selectedBranch = adminBranch;

      const branchKey = selectedBranch.name
        .trim()
        .replace(/\s+/g, "_")
        .toLowerCase();

      // 2️⃣ Find / create patient FIRST
      const normalizedName = normalizeName(name);

      let vpjcId = "";
      let patientDocId = "";

      const patientQuery = query(
        collection(db, "patients"),
        where("mobile", "==", mobile),
        where("normalized_name", "==", normalizedName),
      );

      const patientSnap = await getDocs(patientQuery);

      if (!patientSnap.empty) {
        const data = patientSnap.docs[0].data();
        vpjcId = data.vpjc_id;
        patientDocId = patientSnap.docs[0].id;
        await updateDoc(doc(db, "patients", patientDocId), {
          branch_name: selectedBranch.name,
        });
      } else {
        let unique = false;

        while (!unique) {
          const randomId = generateRandomNumericVPJCId();

          const checkQuery = query(
            collection(db, "patients"),
            where("vpjc_id", "==", randomId),
          );

          const checkSnap = await getDocs(checkQuery);

          if (checkSnap.empty) {
            vpjcId = randomId;
            unique = true;
          }
        }

        const patientRef = await addDoc(collection(db, "patients"), {
          vpjc_id: vpjcId,
          name,
          normalized_name: normalizedName,
          mobile,
          referred_by: referredBy || "",
          visit_count: 0,
          branch_name: selectedBranch.name,
          package_status: "ACTIVE",
          created_at: serverTimestamp(),
        });

        patientDocId = patientRef.id;
      }

      const finalVisitingFor =
        visitingFor === "OTHER" ? visitingForOther.trim() : visitingFor;

      // 3️⃣ ATOMIC: counter + visit
      const counterRef = doc(db, "counters", `visits_${branchKey}_${today}`);
      let tokenNumber;
      let isFreeReview = false;

      if (visitType === "REVIEW") {
        const lastVisitQuery = query(
          collection(db, "visits"),
          where("patient_id", "==", patientDocId),
        );

        const lastVisitSnap = await getDocs(lastVisitQuery);

        if (!lastVisitSnap.empty) {
          const visits = lastVisitSnap.docs
            .map((d) => d.data())
            .sort((a, b) => b.visit_date.localeCompare(a.visit_date));

          const lastVisitDate = new Date(visits[0].visit_date);
          const todayDate = new Date(today);

          const diffDays = Math.floor(
            (todayDate - lastVisitDate) / (1000 * 60 * 60 * 24),
          );

          if (diffDays <= 7) {
            isFreeReview = true;
          }
        }
      }
      await runTransaction(db, async (transaction) => {
        // 🔹 REFS
        const counterRef = doc(db, "counters", `visits_${branchKey}_${today}`);
        const patientRef = doc(db, "patients", patientDocId);
        const visitRef = doc(collection(db, "visits"));
        console.log("Creating token...");
        // 🔹 ALL READS FIRST (VERY IMPORTANT)
        const counterSnap = await transaction.get(counterRef);
        const patientSnap = await transaction.get(patientRef);
        console.log("Token created successfully");
        const p = patientSnap.exists() ? patientSnap.data() : null;

        let incrementPackage = false;
        let usedDays = p?.package_used_days || 0;

        if (
          p &&
          p.active_package === true &&
          visitType === "THERAPY" &&
          usedDays < p.package_total_days
        ) {
          incrementPackage = true;

          // 🔒 ensure only one increment per token
          usedDays = Number(usedDays) + 1;
        }

        // 🔹 TOKEN NUMBER
        let tokenNumber;
        if (!counterSnap.exists()) {
          tokenNumber = 1;
          transaction.set(counterRef, { last_token: 1 });
        } else {
          tokenNumber = counterSnap.data().last_token + 1;
          transaction.update(counterRef, { last_token: tokenNumber });
        }

        // 🔹 VISIT COUNT
        const currentVisitCount = patientSnap.exists()
          ? patientSnap.data().visit_count || 0
          : 0;

        // 🔹 WRITE VISIT
        const isTreatment = p?.treatment_active;
        const isPackage = incrementPackage;

        transaction.set(visitRef, {
          patient_id: patientDocId,
          vpjc_id: vpjcId,
          name,
          mobile,
          visit_count: currentVisitCount + 1,
          visit_date: today,
          token_number: tokenNumber,
          branch_name: selectedBranch.name,
          created_by: "ADMIN",
          visiting_for: finalVisitingFor,
          visit_type: visitType,
          payment_mode: "",
          therapist: "",
          referred_by: referredBy || "",
          // ✅ UPDATED BLOCK
          treatment: isTreatment
            ? p?.current_treatment_name || "Treatment"
            : "",

          visit_category: isTreatment
            ? "TREATMENT"
            : isPackage
              ? "PACKAGE"
              : "CONSULTATION",

          consultation_payment_status: isTreatment
            ? "TREATMENT"
            : isPackage
              ? "PACKAGE"
              : isFreeReview
                ? "FREE_REVIEW"
                : "UNPAID",

          consultation_fee: isFreeReview ? 0 : 0,

          treatment_amount: isTreatment ? 0 : null,
          treatment_payment_status: isTreatment ? "UNPAID" : null,

          package_amount: incrementPackage ? p.package_amount || 0 : 0,
          used_from_package: incrementPackage || false,
          package_id: incrementPackage ? p?.active_package_id || null : null,
          package_start_date: incrementPackage
            ? p?.package_start_date || null
            : null,
          package_used_snapshot: incrementPackage ? usedDays : null,

          created_at: serverTimestamp(),
        });
        if (incrementPackage) {
          const isExpired = usedDays >= p.package_total_days;

          transaction.update(patientRef, {
            package_used_days: usedDays,
            active_package: !isExpired,
            package_status: isExpired ? "COMPLETED" : "ACTIVE",
            package_expired_at: isExpired ? serverTimestamp() : null,
          });
        }

        // 🔹 UPDATE PATIENT VISIT COUNT (SAFE)
        transaction.set(
          patientRef,
          { visit_count: increment(1) },
          { merge: true },
        );
      });

      // 4️⃣ Reset UI
      setName("");
      setMobile("");
      setVisitingFor("");
      setVisitType("");
      setReferredBy("");
      setShowCreate(false);
    } catch (err) {
      console.error("🔥 CREATE TOKEN ERROR:", err);

      setLocationError(
        err?.message ||
          JSON.stringify(err) ||
          "Unknown error while creating token",
      );
    }

    setLoading(false);
  };

  const checkExistingPatient = async () => {
    // 🔒 Mobile validation
    if (!/^[6-9]\d{9}$/.test(mobile)) {
      setMobileError("Please enter a valid 10-digit mobile number");
      return;
    }

    setCheckingPatient(true);
    setMobileError("");

    try {
      const q = query(
        collection(db, "patients"),
        where("mobile", "==", mobile),
      );

      const snap = await getDocs(q);

      if (snap.empty) {
        setMobileError("No patient found with this number");
        setCheckingPatient(false);
        return;
      }

      // MULTIPLE MATCHES
      if (snap.docs.length > 1) {
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setMatchedPatients(list);
        matchedPatientsBackup.current = list;
        setCheckingPatient(false);
        return;
      }

      // SINGLE MATCH
      const p = snap.docs[0].data();
      setName(p.name);
      setPatientFound(true);
      setVisitingFor("SELF");
      setMatchedPatients([]);
    } catch (err) {
      console.error("Admin patient lookup error:", err);
      setMobileError("Error fetching patient. Try again.");
    }

    setCheckingPatient(false);
  };

  const resetCreateFlow = () => {
    // ✅ Close modal
    setShowCreate(false);

    // ✅ Reset flow
    setVisitMode("");
    setPatientFound(false);
    setMatchedPatients([]);
    matchedPatientsBackup.current = [];

    // ✅ Reset form fields
    setName("");
    setMobile("");
    setVisitingFor("");
    setVisitingForOther("");
    setReferredBy("");
    setVisitType("");

    // ✅ Clear all errors
    setMobileError("");
    setCreateErrors({});
    setLocationError("");
  };

  const handleCreateBack = () => {
    // 1️⃣ From FORM → back to MULTIPLE PATIENTS (if existed)
    if (patientFound && matchedPatientsBackup.current.length > 0) {
      setPatientFound(false);
      setMatchedPatients(matchedPatientsBackup.current);
      return;
    }

    // 2️⃣ From MULTIPLE PATIENTS → back to MOBILE
    if (matchedPatients.length > 0) {
      setMatchedPatients([]);
      return;
    }

    // 3️⃣ From FORM (NEW PATIENT) → back to OLD / NEW
    if (visitMode === "NEW") {
      setVisitMode("");
      setPatientFound(false);
      setName("");
      setMobile("");
      setVisitingFor("");
      setVisitType("");
      setReferredBy("");
      return;
    }

    // 4️⃣ From MOBILE → back to OLD / NEW
    if (visitMode === "OLD") {
      setVisitMode("");
      setMobile("");
      setMobileError("");
      return;
    }
  };

  const filteredVisits = visits.filter((v) => {
    const searchText = search.toLowerCase().trim();

    // 🗓️ Convert visit date properly

    // ================== DATE FILTER ==================

    // ================== DATE FILTER ==================

    const visitDate = v.visit_date; // already YYYY-MM-DD

    // ✅ Only start date
    if (selectedDate && !endDate) {
      if (visitDate !== selectedDate) return false;
    }

    // ✅ Start + End range
    if (selectedDate && endDate) {
      let start = selectedDate;
      let end = endDate;

      // auto swap if wrong
      if (end < start) {
        [start, end] = [end, start];
      }

      if (visitDate < start || visitDate > end) return false;
    }

    // ================== SEARCH ==================

    if (!searchText) return true;

    const tokenStr = `t-${String(v.token_number).padStart(3, "0")}`;
    const name = v.name?.toLowerCase() || "";
    const mobile = v.mobile || "";

    const vpjcRaw = v.vpjc_id || "";
    const normalizedVPJC = vpjcRaw.toLowerCase().replace(/-/g, "");
    const normalizedSearch = searchText.replace(/-/g, "");

    return (
      tokenStr.includes(searchText) ||
      String(v.token_number).includes(searchText) ||
      name.includes(searchText) ||
      mobile.includes(searchText) ||
      normalizedVPJC.includes(normalizedSearch)
    );
  });
  const { fromDate, toDate } = getDateRange();

  const filteredTreatmentEntries = todayTreatmentEntries.filter((t) => {
    const d = t.date?.toDate ? t.date.toDate() : new Date(t.date);

    const date = d.toISOString().split("T")[0];

    return date >= fromDate && date <= toDate;
  });

  const filteredPackagePayments = todayPackagePayments.filter((p) => {
    const d = p.created_at?.toDate
      ? p.created_at.toDate()
      : new Date(p.created_at);

    const date = d.toISOString().split("T")[0];

    return date >= fromDate && date <= toDate;
  });

  const paginatedVisits = filteredVisits.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  const fetchAllVisitsForPackage = async (patientId) => {
    const q = query(
      collection(db, "visits"),
      where("patient_id", "==", patientId),
    );

    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  };

  const getRemainingSessions = (visit) => {
    if (!visit.package_id) return null;

    const total = visit.package_total_days || 0;

    const used = visit.package_used_snapshot ?? visit.package_used_days ?? 0;

    const remaining = Math.max(total - used, 0);

    if (remaining === 0) {
      return "Package Completed";
    }

    return `${remaining} ${remaining === 1 ? "day left" : "days left"}`;
  };

  return (
    <>
      <div className="space-y-4 bg-gray-100 min-h-screen p-4">
        {/* Header + Button */}
        <TokenQueueHeader
          filter={filter}
          setFilter={setFilter}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          endDate={endDate}
          setEndDate={setEndDate}
          branchFilter={branchFilter}
          setBranchFilter={setBranchFilter}
          search={search}
          setSearch={setSearch}
          filteredVisits={filteredVisits}
          filteredTreatmentEntries={filteredTreatmentEntries}
          filteredPackagePayments={filteredPackagePayments}
          exportTokensToExcel={exportTokensToExcel}
          resetCreateFlow={resetCreateFlow}
          setShowCreate={setShowCreate}
        />
        <TokenSummaryCards
          visits={filteredVisits}
          treatmentEntries={filteredTreatmentEntries}
          packagePayments={filteredPackagePayments}
        />
        {/* Table */}
        <div className="overflow-x-auto bg-white rounded-xl p-4 shadow-md border border-gray-200">
          {detectingBranch ? (
            <p className="text-center text-sm text-gray-500 py-6">
              Detecting branch location…
            </p>
          ) : filteredVisits.length === 0 ? (
            <p className="text-center text-sm text-gray-500 py-6">
              No matching tokens found
            </p>
          ) : (
            <table className="w-full bg-white text-sm min-w-[900px] text-center">
              <thead>
                <tr className="text-center border-b text-[#204080]">
                  <th className="py-2 whitespace-nowrap">Token</th>
                  <th className="whitespace-nowrap">VPJC ID</th>
                  <th className="whitespace-nowrap">Name</th>
                  <th className="whitespace-nowrap">Mobile</th>
                  <th className="whitespace-nowrap">Visiting For</th>
                  <th className="whitespace-nowrap">Visit Type</th>
                  {!branchFilter && (
                    <th className="whitespace-nowrap">Branch</th>
                  )}
                  <th className="whitespace-nowrap">Time</th>
                  <th className="whitespace-nowrap">Payment</th>
                  <th className="whitespace-nowrap">Details</th>
                  <th className="whitespace-nowrap">Payment Mode</th>
                  <th className="whitespace-nowrap">Therapist</th>
                  <th className="whitespace-nowrap">Action</th>
                  <th className="whitespace-nowrap">Visits</th>
                </tr>
              </thead>

              <tbody>
                {filteredVisits.map((v) => (
                  <tr
                    key={v.id}
                    className="border-b hover:bg-slate-100 text-xs sm:text-sm"
                  >
                    <td className="py-2 font-semibold whitespace-nowrap text-center">
                      T-{String(v.token_number).padStart(3, "0")}
                    </td>

                    <td className="whitespace-nowrap">
                      <button
                        className="text-blue-600 underline hover:text-blue-800"
                        onClick={() => navigateToPatientPage(v.vpjc_id)}
                      >
                        {v.vpjc_id}
                      </button>
                    </td>

                    <td className="whitespace-nowrap">{v.name}</td>
                    <td className="whitespace-nowrap">{v.mobile}</td>
                    <td className="whitespace-nowrap">{v.visiting_for}</td>
                    <td className="whitespace-nowrap">{v.visit_type}</td>
                    {!branchFilter && (
                      <td className="whitespace-nowrap">
                        {v.branch_name?.replace(/\s*branch\s*/i, "")}
                      </td>
                    )}

                    <td className="whitespace-nowrap">
                      {v.created_at?.toDate?.().toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="whitespace-nowrap text-center">
                      <div className="flex flex-col items-center gap-1">
                        {/* PAYMENT DROPDOWN */}
                        <select
                          value={v.consultation_payment_status || "UNPAID"}
                          disabled={v.used_from_package === true}
                          onChange={async (e) => {
                            const newStatus = e.target.value;

                            const visitRef = doc(db, "visits", v.id);

                            await updateDoc(visitRef, {
                              consultation_payment_status: newStatus,
                              consultation_fee:
                                newStatus === "UNPAID"
                                  ? 0
                                  : v.consultation_fee || null,
                              updated_at: serverTimestamp(),
                            });
                          }}
                          className={`text-xs font-semibold rounded-md px-2 py-1 border
                        ${
                          v.consultation_payment_status === "PACKAGE"
                            ? "text-blue-700 border-blue-300 bg-blue-50 cursor-not-allowed"
                            : v.consultation_payment_status === "PAID"
                              ? "text-green-700 border-green-300"
                              : v.consultation_payment_status === "FREE_REVIEW"
                                ? "text-green-700 border-green-300 bg-green-50"
                                : "text-red-700 border-red-300"
                        }`}
                        >
                          {/* 🔥 If package active → only show PACKAGE */}
                          {v.consultation_payment_status === "PACKAGE" ? (
                            <option value="PACKAGE">Package</option>
                          ) : v.consultation_payment_status === "TREATMENT" ? (
                            <option value="TREATMENT">Treatment</option>
                          ) : (
                            <>
                              <option value="UNPAID">Unpaid</option>
                              <option value="PAID">Paid</option>
                              <option value="FREE_REVIEW">Review</option>
                            </>
                          )}
                        </select>
                      </div>
                    </td>
                    <td className="whitespace-nowrap text-center">
                      {v.consultation_payment_status === "PACKAGE" &&
                      v.package_total_days ? (
                        <span className="text-xs font-semibold text-blue-600">
                          {getRemainingSessions(v)}
                        </span>
                      ) : (
                        <input
                          type="text"
                          inputMode="numeric"
                          value={
                            inlineDetails.hasOwnProperty(v.id)
                              ? inlineDetails[v.id]
                              : v.consultation_payment_status === "PAID"
                                ? v.consultation_fee || ""
                                : v.consultation_payment_status === "TREATMENT"
                                  ? v.treatment_amount || ""
                                  : ""
                          }
                          disabled={
                            !["PAID", "TREATMENT"].includes(
                              v.consultation_payment_status,
                            ) ||
                            (v.consultation_payment_status === "PAID" &&
                              v.consultation_fee > 0) ||
                            (v.consultation_payment_status === "TREATMENT" &&
                              v.treatment_amount > 0)
                          }
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, "");
                            setInlineDetails((prev) => ({
                              ...prev,
                              [v.id]: value,
                            }));
                          }}
                          onBlur={async () => {
                            // 🛑 no value → skip
                            if (!inlineDetails[v.id]) return;

                            const amount = Number(inlineDetails[v.id] || 0);
                            console.log("VISIT ID:", v.id);
                            console.log("AMOUNT:", amount);
                            // ================== TREATMENT ==================
                            if (v.visit_category === "TREATMENT") {
                              // 1️⃣ update visit
                              await updateDoc(doc(db, "visits", v.id), {
                                treatment_amount: amount,
                                treatment_payment_status: "PAID",
                                updated_at: serverTimestamp(),
                              });

                              // 2️⃣ optimistic UI update (🔥 avoid override)
                              setVisits((prev) =>
                                prev.map((item) =>
                                  item.id === v.id
                                    ? {
                                        ...item,
                                        treatment_amount: amount,
                                      }
                                    : item,
                                ),
                              );

                              // 3️⃣ get active treatment
                              const tSnap = await getDocs(
                                query(
                                  collection(db, "treatments"),
                                  where("patient_id", "==", v.patient_id),
                                  where("status", "==", "ACTIVE"),
                                ),
                              );

                              let treatmentId = null;

                              if (!tSnap.empty) {
                                treatmentId = tSnap.docs[0].id;
                              }

                              if (!treatmentId) return;

                              // ================== 🔥 PER VISIT ENTRY ==================

                              // 🔍 check if entry already exists for THIS visit
                              const existingSnap = await getDocs(
                                query(
                                  collection(db, "treatment_entries"),
                                  where("visit_id", "==", v.id),
                                ),
                              );

                              if (!existingSnap.empty) {
                                // ✅ update existing entry
                                const docId = existingSnap.docs[0].id;

                                await updateDoc(
                                  doc(db, "treatment_entries", docId),
                                  {
                                    amount: amount,
                                    updated_at: serverTimestamp(),
                                  },
                                );
                              } else {
                                // ✅ create new entry (PER VISIT)
                                await addDoc(
                                  collection(db, "treatment_entries"),
                                  {
                                    treatment_id: treatmentId,
                                    patient_id: v.patient_id,
                                    visit_id: v.id, // 🔥 KEY FIX
                                    amount: amount,
                                    branch_name: v.branch_name,
                                    date: new Date(),
                                    created_at: serverTimestamp(),
                                  },
                                );
                              }
                            }

                            // ================== CONSULTATION ==================
                            else if (
                              v.visit_category !== "TREATMENT" &&
                              v.consultation_payment_status === "PAID"
                            ) {
                              await updateDoc(doc(db, "visits", v.id), {
                                consultation_fee: amount,
                                updated_at: serverTimestamp(),
                              });

                              // 🔥 optimistic update
                              setVisits((prev) =>
                                prev.map((item) =>
                                  item.id === v.id
                                    ? {
                                        ...item,
                                        consultation_fee: amount,
                                      }
                                    : item,
                                ),
                              );
                            }

                            // ================== CLEAR INLINE ==================
                            setInlineDetails((prev) => {
                              const updated = { ...prev };
                              delete updated[v.id];
                              return updated;
                            });
                          }}
                          placeholder={
                            v.consultation_payment_status === "TREATMENT"
                              ? "Treatment Amount"
                              : "Consultation Fee"
                          }
                          className={`max-w-[90px] text-xs border rounded-md px-2 py-1 text-center
        ${
          (v.consultation_payment_status === "PAID" &&
            v.consultation_fee > 0) ||
          (v.consultation_payment_status === "TREATMENT" &&
            v.treatment_amount > 0)
            ? "bg-gray-100 cursor-not-allowed"
            : ""
        }`}
                        />
                      )}
                    </td>
                    <td className="whitespace-nowrap text-center">
                      <select
                        value={v.payment_mode || ""}
                        disabled={
                          !["PAID", "TREATMENT"].includes(
                            v.consultation_payment_status,
                          )
                        }
                        onChange={async (e) => {
                          await updateDoc(doc(db, "visits", v.id), {
                            payment_mode: e.target.value,
                            updated_at: serverTimestamp(),
                          });
                        }}
                        className={`text-xs font-semibold rounded-md px-2 py-1 border
      ${
        !["PAID", "TREATMENT"].includes(v.consultation_payment_status)
          ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
          : v.payment_mode === "ONLINE"
            ? "text-blue-700 border-blue-300"
            : v.payment_mode === "CASH"
              ? "text-green-700 border-green-300"
              : "text-gray-700 border-gray-300"
      }`}
                      >
                        <option value="">Select</option>
                        <option value="CASH">Cash</option>
                        <option value="ONLINE">Online</option>
                      </select>
                    </td>
                    <td className="whitespace-nowrap text-center">
                      <select
                        value={v.therapist || ""}
                        onChange={async (e) => {
                          await updateDoc(doc(db, "visits", v.id), {
                            therapist: e.target.value,
                            updated_at: serverTimestamp(),
                          });
                        }}
                        title={v.therapist}
                        className="w-28 max-w-28 text-xs font-semibold rounded-md px-2 py-1 border border-gray-300 text-gray-700 overflow-hidden text-ellipsis whitespace-nowrap"
                      >
                        <option value="">Therapist</option>
                        <option value="Dr Harish Jasti">Dr Harish Jasti</option>
                        {clinicData.teamMembers.map((teamMember) => (
                          <option key={teamMember.id} value={teamMember.name}>
                            {teamMember.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="space-y-1">
                      <button
                        onClick={async () => {
                          const docRef = doc(db, "patients", v.patient_id);
                          const docSnap = await getDoc(docRef);

                          if (docSnap.exists()) {
                            setEditing({
                              ...v,
                              // 🔥 ENSURE consultation_payment_status is preserved correctly
                              consultation_payment_status:
                                v.consultation_payment_status || "UNPAID",
                              consultation_fee: v.consultation_fee || 0,
                            });
                          }
                        }}
                      >
                        Edit
                      </button>
                    </td>

                    <td className="whitespace-nowrap text-center font-semibold">
                      {v.patient_total_visits ?? 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div
            className="bg-white rounded-xl p-6 w-[95%] sm:w-full max-w-sm
            max-h-[90vh] overflow-y-auto animate-fade-in-up"
          >
            <h3 className="text-lg font-semibold mb-8 text-[#204080]">
              Edit Token
            </h3>

            <form className="space-y-10">
              {/* Patient Name */}
              <div className="relative">
                <input
                  type="text"
                  value={editing.name}
                  onChange={(e) => {
                    setEditing({
                      ...editing,
                      name: e.target.value.replace(/[^a-zA-Z\s]/g, ""),
                    });
                    setEditErrors((p) => ({ ...p, name: "" }));
                  }}
                  placeholder=" "
                  className={`peer w-full bg-transparent border-b-2 py-2 text-base focus:outline-none
            ${
              editErrors.name
                ? "border-red-500 focus:border-red-500"
                : "border-gray-300 focus:border-[#204080]"
            }`}
                />

                <label
                  className={`pointer-events-none absolute left-0 transition-all
          top-[-0.5rem]
          text-sm
          peer-placeholder-shown:top-2
          peer-placeholder-shown:text-base
          peer-placeholder-shown:text-black
          peer-focus:top-[-0.5rem]
          peer-focus:text-sm
          peer-focus:text-[#204080]
          ${editErrors.name ? "text-red-500" : ""}`}
                >
                  Patient Name *
                </label>

                {editErrors.name && (
                  <p className="text-xs text-red-500 mt-1">{editErrors.name}</p>
                )}
              </div>

              {/* Mobile Number */}
              <div className="relative">
                <input
                  type="tel"
                  value={editing.mobile}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 10) {
                      setEditing({ ...editing, mobile: value });
                      setEditErrors((p) => ({ ...p, mobile: "" }));
                    }
                  }}
                  placeholder=" "
                  className={`peer w-full bg-transparent border-b-2 py-2 text-base focus:outline-none
            ${
              editErrors.mobile
                ? "border-red-500 focus:border-red-500"
                : "border-gray-300 focus:border-[#204080]"
            }`}
                />

                <label
                  className={`pointer-events-none absolute left-0 transition-all
          top-[-0.5rem]
          text-sm
          peer-placeholder-shown:top-2
          peer-placeholder-shown:text-base
          peer-placeholder-shown:text-black
          peer-focus:top-[-0.5rem]
          peer-focus:text-sm
          peer-focus:text-[#204080]
          ${editErrors.mobile ? "text-red-500" : ""}`}
                >
                  Mobile Number *
                </label>

                {editErrors.mobile && (
                  <p className="text-xs text-red-500 mt-1">
                    {editErrors.mobile}
                  </p>
                )}
              </div>

              {/* Visiting For */}
              <div>
                <select
                  value={editing.visiting_for}
                  onChange={(e) => {
                    setEditing({
                      ...editing,
                      visiting_for: e.target.value,
                      visiting_for_other: "",
                    });
                    setEditErrors((p) => ({ ...p, visiting_for: "" }));
                  }}
                  className={`w-full bg-transparent border-b-2 py-2 focus:outline-none
            ${
              editErrors.visiting_for
                ? "border-red-500"
                : "border-gray-300 focus:border-[#204080]"
            }`}
                >
                  <option value="">Visiting For *</option>
                  <option value="SELF">Self</option>
                  <option value="CHILD">Child</option>
                  <option value="FAMILY">Family Member</option>
                  <option value="OTHER">Other</option>
                </select>

                {editErrors.visiting_for && (
                  <p className="text-xs text-red-500 mt-1">
                    {editErrors.visiting_for}
                  </p>
                )}

                {/* Other input */}
                {editing.visiting_for === "OTHER" && (
                  <div className="relative mt-4">
                    <input
                      type="text"
                      value={editing.visiting_for_other || ""}
                      onChange={(e) => {
                        setEditing({
                          ...editing,
                          visiting_for_other: e.target.value,
                        });
                        setEditErrors((p) => ({
                          ...p,
                          visiting_for_other: "",
                        }));
                      }}
                      placeholder=" "
                      className={`peer w-full bg-transparent border-b-2 py-2 text-base focus:outline-none
                ${
                  editErrors.visiting_for_other
                    ? "border-red-500 focus:border-red-500"
                    : "border-gray-300 focus:border-[#204080]"
                }`}
                    />

                    <label
                      className={`pointer-events-none absolute left-0 transition-all
                top-[-0.5rem]
                text-sm
                peer-placeholder-shown:top-2
                peer-placeholder-shown:text-base
                peer-placeholder-shown:text-black
                peer-focus:top-[-0.5rem]
                peer-focus:text-sm
                peer-focus:text-[#204080]
                ${editErrors.visiting_for_other ? "text-red-500" : ""}`}
                    >
                      Specify Other *
                    </label>

                    <p className="min-h-[16px] text-xs text-red-500 mt-1">
                      {editErrors.visiting_for_other || ""}
                    </p>
                  </div>
                )}
              </div>

              {/* Visit Type */}
              <div>
                <select
                  value={editing.visit_type}
                  onChange={(e) => {
                    setEditing({ ...editing, visit_type: e.target.value });
                    setEditErrors((p) => ({ ...p, visit_type: "" }));
                  }}
                  className={`w-full bg-transparent border-b-2 py-2 focus:outline-none
            ${
              editErrors.visit_type
                ? "border-red-500"
                : "border-gray-300 focus:border-[#204080]"
            }`}
                >
                  <option value="">Type of Visit *</option>
                  <option value="NEW">New Consultation</option>
                  <option value="REVISIT">Revisit</option>
                  <option value="REVIEW">Review / Follow-up</option>
                  <option value="THERAPY">Therapy Session</option>
                </select>
                <p className="mt-2 text-xs text-gray-500">
                  {visitType
                    ? VISIT_TYPE_HELPER[visitType]
                    : "Select the purpose of your visit today"}
                </p>
                {editErrors.visit_type && (
                  <p className="text-xs text-red-500 mt-1">
                    {editErrors.visit_type}
                  </p>
                )}
              </div>

              {/* Payment Status (READ ONLY) */}
              <div className="text-sm font-semibold text-gray-700">
                Payment Status:
                <span className="ml-2 text-[#204080]">
                  {editing.consultation_payment_status === "PACKAGE"
                    ? "Package (Auto Applied)"
                    : editing.consultation_payment_status === "TREATMENT"
                      ? "Treatment"
                      : editing.consultation_payment_status === "FREE_REVIEW"
                        ? "Review"
                        : editing.consultation_payment_status === "PAID"
                          ? "Paid"
                          : "Unpaid"}
                </span>
              </div>
              {/* 🔥 Payment Amount (Editable only if PAID) */}
              {editing.consultation_payment_status === "PAID" && (
                <div className="relative">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={editing.consultation_fee || ""}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, "");
                      setEditing({
                        ...editing,
                        consultation_fee: value,
                      });
                    }}
                    placeholder=" "
                    className="peer w-full bg-transparent border-b-2 py-2 text-base focus:outline-none border-gray-300 focus:border-[#204080]"
                  />

                  <label
                    className="pointer-events-none absolute left-0 transition-all
      top-[-0.5rem] text-sm text-[#204080]"
                  >
                    Payment Amount (₹)
                  </label>
                </div>
              )}
              {editing.visit_category === "TREATMENT" && (
                <div className="relative">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={editing.treatment_amount || ""}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, "");
                      setEditing({
                        ...editing,
                        treatment_amount: value,
                      });
                    }}
                    placeholder=" "
                    className="peer w-full bg-transparent border-b-2 py-2 text-base focus:outline-none border-gray-300 focus:border-[#204080]"
                  />

                  <label
                    className="pointer-events-none absolute left-0 transition-all
      top-[-0.5rem] text-sm text-[#204080]"
                  >
                    Treatment Amount (₹)
                  </label>
                </div>
              )}

              {/* 🧾 Reason for Amount Change (Audit) */}
              {/* 🧾 Reason (MANDATORY - Compact UI) */}
              {(editing.consultation_payment_status === "PAID" ||
                editing.treatment_payment_status === "PAID") && (
                <div className="relative">
                  <input
                    type="text"
                    value={editing.amount_change_reason || ""}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        amount_change_reason: e.target.value,
                      })
                    }
                    placeholder=" "
                    className={`peer w-full bg-transparent border-b-2 py-2 text-base focus:outline-none
        ${
          editErrors.amount_change_reason
            ? "border-red-500 focus:border-red-500"
            : "border-gray-300 focus:border-[#204080]"
        }`}
                  />

                  <label
                    className={`pointer-events-none absolute left-0 transition-all
        top-[-0.5rem]
        text-sm
        peer-placeholder-shown:top-2
        peer-placeholder-shown:text-base
        peer-placeholder-shown:text-black
        peer-focus:top-[-0.5rem]
        peer-focus:text-sm
        peer-focus:text-[#204080]
        ${editErrors.amount_change_reason ? "text-red-500" : ""}`}
                  >
                    Reason for Amount Change *
                  </label>

                  {editErrors.amount_change_reason && (
                    <p className="text-xs text-red-500 mt-1">
                      {editErrors.amount_change_reason}
                    </p>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setEditing(null);
                    setEditErrors({});
                  }}
                  className="px-4 py-2 rounded-lg border"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleUpdate}
                  className="px-5 py-2 rounded-full text-white font-semibold
            bg-[#204080] hover:opacity-90 transition"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          onClick={resetCreateFlow} // ✅ click outside closes
        >
          <div
            className="bg-white rounded-xl p-6 w-[95%] sm:w-full max-w-sm animate-fade-in-up"
            onClick={(e) => e.stopPropagation()} // 🔒 VERY IMPORTANT
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-[#204080]">
                Create New Token
              </h3>

              <button
                type="button"
                onClick={resetCreateFlow}
                className="text-gray-500 hover:text-gray-800 text-xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {locationError && (
              <div className="mb-3 rounded-lg bg-red-100 text-red-700 px-4 py-2 text-sm">
                {locationError}
              </div>
            )}

            {visitMode && (
              <button
                type="button"
                onClick={handleCreateBack}
                className="mb-4 text-sm text-[#204080] font-medium"
              >
                ← Back
              </button>
            )}

            {!visitMode && (
              <div className="space-y-6 text-center">
                <h3 className="text-lg font-semibold text-[#204080]">
                  Has the patient visited before?
                </h3>

                <button
                  type="button"
                  onClick={() => {
                    // resetCreateFlow();
                    setVisitMode("OLD");
                    setMobile("");
                  }}
                  className="w-full rounded-full border border-[#204080] py-2"
                >
                  Old Patient
                </button>

                <button
                  type="button"
                  onClick={() => {
                    // resetCreateFlow();   // ✅ clear old patient residue
                    setVisitMode("NEW");
                    setVisitType("NEW");
                  }}
                  className="w-full rounded-full bg-[#204080] text-white py-2"
                >
                  New Patient
                </button>
              </div>
            )}

            {visitMode === "OLD" &&
              !patientFound &&
              matchedPatients.length === 0 && (
                <div className="space-y-6">
                  <div className="relative">
                    <input
                      type="tel"
                      value={mobile}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, "");
                        if (v.length <= 10) {
                          setMobile(v);
                          setMobileError("");
                        }
                      }}
                      placeholder=" "
                      className="peer w-full border-b-2 py-2 focus:outline-none"
                    />
                    <label
                      className="pointer-events-none absolute left-0 transition-all
            top-[-0.5rem]
            text-sm
            peer-placeholder-shown:top-2
            peer-placeholder-shown:text-base
            peer-placeholder-shown:text-black
            peer-focus:top-[-0.5rem]
            peer-focus:text-sm
            peer-focus:text-[#204080]"
                    >
                      Enter Mobile Number
                    </label>
                  </div>

                  {mobileError && (
                    <p className="text-xs text-red-600">{mobileError}</p>
                  )}

                  <button
                    type="button"
                    onClick={checkExistingPatient} // SAME LOGIC AS USER SIDE
                    disabled={checkingPatient}
                    className="w-full rounded-full bg-[#204080] text-white py-2"
                  >
                    {checkingPatient ? "Checking..." : "Find Patient"}
                  </button>
                </div>
              )}
            {matchedPatients.length > 0 && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600 text-center">
                  Multiple patients found. Select one:
                </p>

                {matchedPatients.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      setName(p.name);
                      setPatientFound(true);
                      setMatchedPatients([]); // hide list
                      setVisitingFor("SELF");
                    }}
                    className="w-full border rounded-lg p-3 text-left"
                  >
                    <p className="font-medium">{p.name}</p>
                    <p className="text-xs text-gray-500">{p.vpjc_id}</p>
                  </button>
                ))}
              </div>
            )}
            {(visitMode === "NEW" || patientFound) && (
              <form className="space-y-10">
                {/* Patient Name */}
                <div className="relative">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value.replace(/[^a-zA-Z\s]/g, ""));
                      setCreateErrors((p) => ({ ...p, name: "" }));
                    }}
                    placeholder=" "
                    className={`peer w-full bg-transparent border-b-2 py-2 text-base focus:outline-none
                ${
                  createErrors.name
                    ? "border-red-500 focus:border-red-500"
                    : "border-gray-300 focus:border-[#204080]"
                }`}
                  />

                  <label
                    className={`pointer-events-none absolute left-0 transition-all
            top-[-0.5rem]
            text-sm
            peer-placeholder-shown:top-2
            peer-placeholder-shown:text-base
            peer-placeholder-shown:text-black
            peer-focus:top-[-0.5rem]
            peer-focus:text-sm
            peer-focus:text-[#204080]
                ${createErrors.name ? "text-red-500" : ""}`}
                  >
                    Patient Name *
                  </label>

                  {createErrors.name && (
                    <p className="text-xs text-red-500 mt-1">
                      {createErrors.name || ""}
                    </p>
                  )}
                </div>

                {/* Mobile */}
                <div className="relative">
                  <input
                    type="tel"
                    value={mobile}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      if (value.length <= 10) {
                        setMobile(value);
                        setCreateErrors((p) => ({ ...p, mobile: "" }));
                      }
                    }}
                    placeholder=" "
                    className={`peer w-full bg-transparent border-b-2 py-2 text-base focus:outline-none
                ${
                  createErrors.mobile
                    ? "border-red-500 focus:border-red-500"
                    : "border-gray-300 focus:border-[#204080]"
                }`}
                  />

                  <label
                    className={`pointer-events-none absolute left-0 transition-all
            top-[-0.5rem]
            text-sm
            peer-placeholder-shown:top-2
            peer-placeholder-shown:text-base
            peer-placeholder-shown:text-black
            peer-focus:top-[-0.5rem]
            peer-focus:text-sm
            peer-focus:text-[#204080]
                ${createErrors.mobile ? "text-red-500" : ""}
            `}
                  >
                    Mobile Number *
                  </label>

                  {createErrors.mobile && (
                    <p className="text-xs text-red-500 mt-1">
                      {createErrors.mobile || ""}
                    </p>
                  )}
                </div>

                {/* Visiting For */}
                <div>
                  <select
                    value={visitingFor}
                    disabled={visitMode === "OLD"}
                    onChange={(e) => {
                      setVisitingFor(e.target.value);
                      setVisitingForOther("");
                      setCreateErrors((p) => ({ ...p, visitingFor: "" }));
                    }}
                    className={`w-full bg-transparent border-b-2 py-2 focus:outline-none
                ${visitMode === "OLD" ? "opacity-60 cursor-not-allowed" : ""}
                ${
                  createErrors.visitingFor
                    ? "border-red-500"
                    : "border-gray-300 focus:border-[#204080]"
                }`}
                  >
                    <option value="">Visiting For *</option>

                    {visitMode === "OLD" && <option value="SELF">Self</option>}

                    {visitMode === "NEW" && (
                      <>
                        <option value="SELF">Self</option>
                        <option value="CHILD">Child</option>
                        <option value="FAMILY">Family Member</option>
                        <option value="OTHER">Other</option>
                      </>
                    )}
                  </select>

                  {createErrors.visitingFor && (
                    <p className="text-xs text-red-500 mt-1">
                      {createErrors.visitingFor || ""}
                    </p>
                  )}
                  {/* Show input only when Other is selected */}
                  {visitingFor === "OTHER" && (
                    <div className="relative mt-4">
                      <input
                        type="text"
                        value={visitingForOther}
                        onChange={(e) => {
                          setVisitingForOther(e.target.value);
                          setCreateErrors((p) => ({
                            ...p,
                            visitingForOther: "",
                          }));
                        }}
                        placeholder=" "
                        className={`peer w-full bg-transparent border-b-2 py-2 text-base focus:outline-none
                    ${
                      createErrors.visitingForOther
                        ? "border-red-500 focus:border-red-500"
                        : "border-gray-300 focus:border-[#204080]"
                    }`}
                      />

                      <label
                        className={`pointer-events-none absolute left-0 transition-all
                    top-[-0.5rem]
                    text-sm
                    peer-placeholder-shown:top-2
                    peer-placeholder-shown:text-base
                    peer-placeholder-shown:text-black
                    peer-focus:top-[-0.5rem]
                    peer-focus:text-sm
                    peer-focus:text-[#204080]
                    ${createErrors.visitingForOther ? "text-red-500" : ""}
                  `}
                      >
                        Specify Other *
                      </label>

                      <p className="min-h-[16px] text-xs text-red-500 mt-1">
                        {createErrors.visitingForOther || ""}
                      </p>
                    </div>
                  )}
                </div>
                {visitType === "NEW" && (
                  <div className="relative">
                    <input
                      type="text"
                      value={referredBy}
                      onChange={(e) => setReferredBy(e.target.value)}
                      placeholder=" "
                      className="peer w-full bg-transparent border-b-2 py-2 focus:outline-none"
                    />

                    <label
                      className="pointer-events-none absolute left-0 transition-all
      top-[-0.5rem]
      text-sm
      peer-placeholder-shown:top-2
      peer-placeholder-shown:text-base
      peer-placeholder-shown:text-black
      peer-focus:top-[-0.5rem]
      peer-focus:text-sm
      peer-focus:text-[#204080]"
                    >
                      Referred By
                    </label>
                  </div>
                )}

                {/* Visit Type */}
                <div>
                  <select
                    value={visitType}
                    onChange={(e) => {
                      setVisitType(e.target.value);
                      setCreateErrors((p) => ({ ...p, visitType: "" }));
                    }}
                    className={`w-full bg-transparent border-b-2 py-2 focus:outline-none
                ${
                  createErrors.visitType
                    ? "border-red-500"
                    : "border-gray-300 focus:border-[#204080]"
                }`}
                  >
                    <option value="">Type of Visit *</option>

                    {visitMode === "NEW" && (
                      <option value="NEW">New Consultation</option>
                    )}

                    {visitMode === "OLD" && (
                      <>
                        <option value="REVISIT">Revisit</option>
                        <option value="REVIEW">Review / Follow-up</option>
                        <option value="THERAPY">Therapy Session</option>
                      </>
                    )}
                  </select>

                  <p className="mt-2 text-xs text-gray-500">
                    {visitType
                      ? VISIT_TYPE_HELPER[visitType]
                      : "Select the purpose of your visit today"}
                  </p>
                  {createErrors.visitType && (
                    <p className="text-xs text-red-500 mt-1">
                      {createErrors.visitType}
                    </p>
                  )}
                </div>
                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      resetCreateFlow(); // ✅ clear everything
                      setShowCreate(false);
                    }}
                    className="px-4 py-2 rounded-lg border"
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    onClick={handleCreateToken}
                    disabled={loading}
                    className="px-5 py-2 rounded-full text-white font-semibold
                bg-[#204080] hover:opacity-90 transition disabled:opacity-60"
                  >
                    {loading ? "Creating..." : "Create Token"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
